import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { BREAKPOINTS_WIDTHS, formatMoney, useWindowSize } from '@belong/common';
import { Button, Text } from '@belong/ui';
import PaymentMethodContainer from 'containers/PaymentMethodContainer/PaymentMethodContainer';
import {
  MaintenancePaymentPlan,
  MaintenancePaymentMethod,
  PaymentMethod,
  PaymentPreference,
  PaymentPriority,
} from 'models/enums';
import { ApprovalContext } from 'pages/PostInspectionFlow/steps/Improvements/Approval/ApprovalContext';
import { QuestionsOrConcernsModalFooter } from 'post-inspection/components/question-or-concern-modal-footer/question-or-concern-modal-footer';
import { STRINGS } from 'post-inspection/constants/strings';
import { selectFullUser } from 'store/redux/user/selectors';
import { getString } from 'strings';

type Props = {
  onNextStepClick: () => void;
  variant: string;
  isLastStep: boolean;
};

export const PaymentMethodStep = ({
  onNextStepClick,
  variant,
  isLastStep,
}: Props) => {
  const {
    homeownerPayment,
    onApprovalStepSubmit,
    homeownerReportPayment,
    setIsSubmitting,
    isLegacyInvoicing,
    isDoNotExceedGroup,
    onNext,
    isComplimentaryRepairsExclusivitySelected,
    canExclusivityBeSelected,
  } = useContext(ApprovalContext);
  const fullUser = useSelector(selectFullUser);
  const { width } = useWindowSize();

  const isInvoicedByGroup = isLegacyInvoicing && isDoNotExceedGroup;

  const isMobile = width <= BREAKPOINTS_WIDTHS.SM;

  const { groupDueNow } = homeownerReportPayment;

  const dueNow =
    canExclusivityBeSelected && isComplimentaryRepairsExclusivitySelected
      ? 1
      : groupDueNow;

  const userPrimaryPaymentMethod = fullUser?.getPaymentMethodFor(
    PaymentPreference.Maintenance,
    (isInvoicedByGroup && PaymentPriority.Backup) || PaymentPriority.Primary,
  );

  const handlePayNow = async () => {
    setIsSubmitting(true);

    const data = { ...homeownerPayment };

    data.deferPaymentUntilLease = null;
    data.installmentCount = 1;
    data.paymentPlan = MaintenancePaymentPlan.OneTime;
    data.paymentMethod =
      userPrimaryPaymentMethod?.paymentMethod === PaymentMethod.Ach
        ? MaintenancePaymentMethod.Check
        : userPrimaryPaymentMethod.paymentMethod;

    try {
      const depositResponse = await onApprovalStepSubmit({
        groupPayment: data,
        ...(canExclusivityBeSelected && {
          group: {
            exclusivityOptedIn: isComplimentaryRepairsExclusivitySelected,
          },
        }),
      });

      if (depositResponse && (isInvoicedByGroup || isLastStep)) {
        onNext({ refreshFlow: true });
      } else {
        onNextStepClick();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex w-full flex-col items-center justify-between pt-xl sm:pt-3xl">
      <div className="flex w-full flex-col items-center">
        <div className="text-center sm:max-w-[340px] md:max-w-[700px]">
          {dueNow > 0 ? (
            <Text fontWeight="semibold" variant="h2">
              {canExclusivityBeSelected &&
              isComplimentaryRepairsExclusivitySelected
                ? getString(STRINGS['payment-method-step-title-exclusivity'])
                : getString(STRINGS['payment-method-step-title'], {
                    dueNow: formatMoney(dueNow, 'DOLLARS_WITH_CENTS'),
                  })}
            </Text>
          ) : (
            <Text fontWeight="semibold" variant="h2">
              {getString(STRINGS['payment-method-step-title-no-due'])}
            </Text>
          )}
        </div>
        <div className="pt-xs text-center sm:max-w-[510px]">
          <Text>
            {canExclusivityBeSelected &&
            isComplimentaryRepairsExclusivitySelected
              ? STRINGS['payment-method-step-subtitle-exclusivity']
              : STRINGS[
                  isInvoicedByGroup
                    ? 'payment-method-step-subtitle-do-not-exceed'
                    : 'payment-method-step-subtitle'
                ]}
          </Text>
        </div>
        <div className="my-xl w-full sm:mb-0">
          <div className="mb-lg px-sm">
            <PaymentMethodContainer
              maintenanceOnly
              defaultPriority={
                isInvoicedByGroup
                  ? PaymentPriority.Backup
                  : PaymentPriority.Primary
              }
              hasLabels={false}
              variant={variant}
            />
          </div>
          <QuestionsOrConcernsModalFooter>
            <Button
              disabled={!userPrimaryPaymentMethod}
              onClick={handlePayNow}
              size={isMobile ? 'fluid' : groupDueNow ? 'default' : 'large'}
            >
              {groupDueNow
                ? STRINGS['pay-now']
                : isLastStep
                  ? STRINGS.done
                  : STRINGS.next}
            </Button>
          </QuestionsOrConcernsModalFooter>
        </div>
      </div>
    </div>
  );
};
